import type { FC, SVGProps } from "react"

import { atom, DefaultValue, selector } from "recoil"

import {
  isOfferedImagerySource,
  isRetiredImagerySource,
} from "~/components/RetiredImagery/retired-imagery"
import { findPanPositionState } from "~/containers/LegacyDesigner/state/scroll"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { safeSiteImageryState } from "~/containers/Proposal/state/site/site-state"
import { ImagerySource } from "~/graphql/client"

import { mapImageToKey } from "./imageElements"

export const zoomState = atom<number>({
  key: `view_zoom`,
  default: 100,
})

export const panState = findPanPositionState(`Studio`)

export const lineWeightState = atom<number>({
  key: `view_lineWeight`,
  default: 3,
})

// Fallback order when a Design's selected source has no image; the last match wins. Bing is
// retired, so it is never picked automatically.
export const imageSources = [
  { key: ImagerySource.nearmap, label: `NearMap` },
  { key: ImagerySource.google, label: `Google` },
  { key: ImagerySource.pictometry, label: `EagleView` },
]

export const imageSourceState = selector<string[]>({
  key: `imagerySelector`,
  get: ({ get }) => {
    const { displayImageryProvider: preferred, source: designSource } =
      get(designLocalState)
    const imagery = [...get(safeSiteImageryState)].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )

    const image = imagery.find((lp) => lp.source === preferred)

    if (image) return [mapImageToKey(image)]

    // A Vision Bing Design without its saved image shows nothing rather than a different
    // background. An Aurora Design's provider is Aurora metadata, so it keeps the fallback.
    if (isRetiredImagerySource(preferred) && designSource !== `aurora`) {
      return [preferred]
    }

    return [
      imageSources
        .map((s) => s.key as string)
        .reduce((source, key) => {
          const image = imagery.find((lp) => lp.source === key)

          if (image) return mapImageToKey(image)

          return source
        }, ImagerySource.nearmap),
    ]
  },
  set: ({ get, set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      console.warn(`Tried to reset imageSourceState.`)

      return
    }

    if (newValue.length !== 1) {
      console.warn(`Expected one imagerySource but got ${newValue.length}`)

      return
    }

    const [newDisplayImageryProvider] = newValue
    const imagery = get(safeSiteImageryState)

    const foundImage = imagery.find(
      (imageryResult) =>
        mapImageToKey(imageryResult) === newDisplayImageryProvider,
    )

    const currentDisplayImageryProvider =
      get(designLocalState).displayImageryProvider
    if (
      foundImage &&
      !isOfferedImagerySource(foundImage.source, currentDisplayImageryProvider)
    ) {
      console.warn(`Bing imagery is retired and cannot be newly selected.`)

      return
    }

    if (foundImage) {
      set(designLocalState, (current) => ({
        ...current,
        displayImageryProvider: foundImage.source,
      }))
    }
  },
})

export const ZOOM_CONTROLS = [`ZoomIn`, `ZoomOut`, `Reset`] as const
export type ZoomControl = (typeof ZOOM_CONTROLS)[number]

export type ZoomControlArray = {
  icon: FC<SVGProps<SVGSVGElement>>
  onClick: () => void
  tooltip: string
}

export const notesDrawerIsOpenState = atom<boolean>({
  key: `notesDrawerIsOpen`,
  default: false,
})

export const moveBoxPositionState = atom({
  key: `moveBoxPosition`,
  default: { x: 0, y: 0 },
})

export const resetLocationModalIsOpenState = atom({
  key: `resetLocationModalIsOpen`,
  default: false,
})

export const isLeftPanelOpenState = atom({
  key: `isLeftPanelOpen`,
  default: true,
})
